import React, { useCallback, useEffect, useState } from 'react'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import questionnaireFetchQuestionnaireDefinition from './fetch/questionnaireFetch.definition'
import { Answer, AnswerSheet, Question, QuestionnaireType } from './questionnaireTypes'
import { QuestionType } from './questionnaireObject'
import QuestionnaireInput from './questionnaireInput.component'
import { useParams } from 'react-router'
import questionnaireSendQuestionnaireAnswerDefinition from './sendAnswer/questionnaireSaveAnswers.definition'
import { toastError, toastSuccess } from '~/components/toasts'

function normalizeQuestion(question: QuestionType): Question | null {
  if (typeof question.id !== 'number' || !question.type) {
    return null
  }

  return {
    id: question.id,
    parentQuestionId: question.parentQuestionId ?? null,
    title: question.title,
    observation: question.observation ?? '',
    type: question.type,
    required: question.required ?? false,
    ordinalPosition: question.ordinalPosition ?? 0,
    deletedAt: question.deletedAt ?? null,
    questionOptions: (question.questionOptions ?? []).flatMap(option =>
      typeof option.id === 'number'
        ? [
            {
              id: option.id,
              label: option.label,
              deletedAt: option.deletedAt ?? null,
            },
          ]
        : [],
    ),
    questionnaireId: question.questionnaireId ?? 0,
    childQuestions: (question.childQuestions ?? [])
      .map(normalizeQuestion)
      .filter((currentQuestion): currentQuestion is Question => currentQuestion !== null),
  }
}

export default function Questionnaire({}) {
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireType | null>(null)
  const [formData, setFormData] = useState<Record<string, any>>({})

  const { questionnaireId: paramsQuestionnaireId } = useParams()

  const [isQuestionnaireLoading, setIsQuestionnaireLoading] = useState(true)
  const { callAction: callActionQuestionnaire } = useActionFunction(
    questionnaireFetchQuestionnaireDefinition,
  )

  const { callAction: callActionSendQuestionnaire } = useActionFunction(
    questionnaireSendQuestionnaireAnswerDefinition,
  )

  const callQuestionnaire = useCallback(
    (questionnaireId: string) => {
      const parsedQuestionnaireId = Number(questionnaireId)
      if (Number.isFinite(parsedQuestionnaireId)) {
        const { resultPromise } = callActionQuestionnaire({
          id: parsedQuestionnaireId,
        })

        resultPromise
          .then(result => {
            const normalizedQuestions = (result.questions ?? [])
              .map(normalizeQuestion)
              .filter((question): question is Question => question !== null)
            const normalizedQuestionnaire: QuestionnaireType = {
              id: result.id,
              title: result.title,
              observation: result.observation ?? '',
              lgpdDisclaimer: result.lgpdDisclaimer ?? '',
              targetType: result.targetType ?? '',
              targetPublic: result.targetPublic ?? '',
              delimitationType: result.delimitationType ?? '',
              delimitationId: result.delimitationId ? Number(result.delimitationId) : null,
              delimitationCustom: result.delimitationCustom ?? null,
              startDate: result.startDate ? new Date(result.startDate) : new Date(),
              endDate: result.endDate ? new Date(result.endDate) : null,
              createdAt: new Date(result.createdAt),
              updatedAt: result.updatedAt ? new Date(result.updatedAt) : null,
              deletedAt: result.deletedAt ? new Date(result.deletedAt) : null,
              questions: normalizedQuestions,
            }
            setQuestionnaire(normalizedQuestionnaire)
            const prepareFormData: Record<string, Answer & Partial<AnswerSheet>> = {}
            function flatQuestions(currentQuestions: Question[]) {
              for (const question of currentQuestions) {
                prepareFormData[String(question.id)] = {
                  questionId: question.id,
                  answerId: null,
                  string: null,
                  boolean: null,
                  numeric: null,
                  array: null,
                  date: null,
                  professionalId: 1,
                  familyId: 1,
                  citizenId: 1,
                }

                if (question.childQuestions && question.childQuestions.length > 0) {
                  flatQuestions(question.childQuestions as Question[])
                }
              }
              return
            }

            flatQuestions(normalizedQuestions)
            setFormData(prepareFormData)
            setIsQuestionnaireLoading(false)
          })
          .catch(error => {
            console.error(error)
            toastError('Erro ao carregar questionário')
          })

        return resultPromise
      }
    },
    [paramsQuestionnaireId],
  )

  const sendQuestionnaire = useCallback(
    (answers: any) => {
      const { resultPromise } = callActionSendQuestionnaire(answers)

      resultPromise
        .then(result => {
          toastSuccess('Respostas enviadas com sucesso')
          console.debug('Respostas Enviadas', { result })
        })
        .catch(error => {
          toastError('Erro ao enviar respostas')
          console.log(error)
        })

      return resultPromise
    },
    [paramsQuestionnaireId],
  )

  useEffect(() => {
    if (paramsQuestionnaireId) {
      callQuestionnaire(paramsQuestionnaireId)
      return () => {}
    }

    if (questionnaire !== null) {
      setIsQuestionnaireLoading(false)
    }

    return () => {}
  }, [paramsQuestionnaireId])

  const handleInputChange = (e: { target: { name: any; value: any } }, value: any) => {
    const name = e.target.name
    const preparedValue = {
      questionId: name,
      ...value,
    }
    const preparedFormData = {
      ...formData,
      [name]: {
        parsedValue: e.target.value,
        ...preparedValue,
      },
    }

    setFormData(preparedFormData)
  }

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    const answersIds = Object.keys(formData)
    const answers = []
    for (const answer of answersIds) {
      answers.push(formData[answer])
    }
    const formRequest = {
      answers: answers,
      professionalId: 1,
      familyId: 1,
      citizenId: 2,
    }
    sendQuestionnaire(formRequest)
    toastSuccess('Respostas enviadas com sucesso')
    console.log('Form submitted:', formRequest)
  }

  return (
    <>
      {isQuestionnaireLoading ? (
        <>Carregando</>
      ) : questionnaire !== null ? (
        <div style={{ margin: 'auto' }}>
          <form>
            <h1>{questionnaire.title}</h1>
            <p>{questionnaire.observation}</p>
            <p>
              <i>{questionnaire.lgpdDisclaimer}</i>
            </p>
            {compileRawQuestions(questionnaire.questions, [], handleInputChange, formData).map(
              item => item,
            )}

            <button type='submit' onClick={handleSubmit}>
              Enviar
            </button>
          </form>
          <div style={{ marginBottom: '25%' }}></div>
        </div>
      ) : (
        <>Questionário não encontrado?</>
      )}
    </>
  )
}

function compileRawQuestions(
  currentQuestions: Question[],
  processedQuestions: React.ReactNode[] = [],
  handleInputChange: Function,
  formData: any,
  isChild = false,
) {
  for (const question of currentQuestions) {
    processedQuestions.push(
      <div
        style={{
          border: !isChild ? '1px black dashed' : '',
          marginBottom: !isChild ? '10px' : '',
        }}
      >
        <p>
          <span style={{ fontWeight: 'bold' }}>{question.title}</span> - {question.type} -{' '}
          {question.required ? 'Required' : 'Optional'}
          <br />
          <span>{question.observation}</span>
        </p>
        {question.questionOptions.map(item => (
          <>{JSON.stringify(item)}</>
        ))}

        <QuestionnaireInput
          name={String(question.id)}
          onChange={handleInputChange}
          type={question.type}
          questionOptions={question.questionOptions}
          formData={formData[question.id]}
        />

        {question.childQuestions && question.childQuestions.length > 0 ? (
          <div>
            <div style={{ marginLeft: '15px' }}>
              {compileRawQuestions(
                question.childQuestions as Question[],
                [],
                handleInputChange,
                formData,
                true,
              ).map(item => item)}
            </div>
          </div>
        ) : null}
      </div>,
    )
  }
  return processedQuestions
}
